/// <amd-module name="Test/ManageTestPlansFilter/TestPlanFilterManager" />
import { TimerManagement } from "VSS/Core/TimerManagement";
import { DirectoryPivotType, Filters, ITestPlanFilterField, TestPlanFilterFieldType } from "Test/ManageTestPlansCommon/Constants";
import { ITestPlanHubSettingService } from "Test/ManageTestPlansCommon/Contracts";
import { ITestPlanFilterManager } from "Test/ManageTestPlansFilter/Contracts";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { IPickListItem } from "VSSUI/PickList";
import { Filter, FILTER_CHANGE_EVENT, IFilter, IFilterItemState, IFilterState } from "VSSUI/Utilities/Filter";
import { HubViewOptionKeys } from "VSSUI/Utilities/HubViewState";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { TelemetryConstants } from "Test/ManageTestPlansCommon/Constants";

export const FILTER_THROTTLE_DELAY = 1000;

export class TestPlanFilterManager extends VssService implements ITestPlanFilterManager {
    public _serviceStart(pageContext: IVssPageContext): void {
        this._pageContext = pageContext;
    }

    public init(
        hubViewState: IVssHubViewState,
        currentPivot: DirectoryPivotType,
        onFilterChanged: (pivot: DirectoryPivotType, filterState: IFilterState) => void
    ): ITestPlanFilterManager {
        this._hubViewState = hubViewState;
        this._onFilterChangedFunc = onFilterChanged.bind(this);
        this._resetInternalFilters();
        this._initializeInternals(hubViewState);
        this._currentPivot = currentPivot;
        this._timerManagementContext = new TimerManagement();
        this._throttledSaveFilter = this._timerManagementContext.throttle(this._setFilterState.bind(this), FILTER_THROTTLE_DELAY);
        return this;
    }

    public getFilter(): IFilter {
        return this._hubViewState.filter;
    }

    public getCurrentPivot(): DirectoryPivotType {
        return this._currentPivot;
    }

    public isFilterOn(activeFilter: IFilter): boolean {
        let isFilterOn: boolean = false;

        for (const filterItem of Filters.AllFilterItems) {
            const filterItemState: IFilterItemState | null = activeFilter.getFilterItemState(filterItem.fieldName);
            isFilterOn = isFilterOn || this.isFilterItemStateOn(filterItemState, filterItem.displayType);
        }
        return isFilterOn;
    }

    public isFilterStateOn(filterState: IFilterState | null): boolean {
        let isFilterStateOn: boolean = false;

        for (const filterItem of Filters.AllFilterItems) {
            if (filterState && filterState.hasOwnProperty(filterItem.fieldName)) {
                isFilterStateOn = isFilterStateOn || this.isFilterItemStateOn(filterState[filterItem.fieldName], filterItem.displayType);

                if (isFilterStateOn) {
                    return isFilterStateOn;
                }
            }
        }
        return isFilterStateOn;
    }

    public isPivotFilterInitialized(pivot: DirectoryPivotType): boolean | null {
        return this._getPivotValue<boolean>(
            pivot,
            "isPivotFilterInitialized",
            () => this._allPivotFilterInitialized,
            () => this._minePivotFilterInitialized
        );
    }

    public getSelectedFilterItemValue(filter: IFilter, field: ITestPlanFilterField): string[] | string | null {
        let filterValue: string[] | string | null = null;

        switch (field.displayType) {
            case TestPlanFilterFieldType.Text:
                const filterTextValue: string = filter.getFilterItemValue(field.fieldName) as string;
                filterValue = filterTextValue && filterTextValue.length > 0 ? filterTextValue : null;
                break;
            case TestPlanFilterFieldType.CheckboxList:
                const filterListValue: IPickListItem[] = filter.getFilterItemValue(field.fieldName) as IPickListItem[];
                filterValue = filterListValue && filterListValue.length > 0 ? filterListValue.map((item) => item.key) : null;
                break;
        }

        return filterValue;
    }

    public getAndResetUserChangedFilter() {
        const filterHasChanged = this.getUserChangedFilter();
        this._filterHasChanged = false;

        return filterHasChanged;
    }

    public getUserChangedFilter(): boolean {
        return this._filterHasChanged;
    }

    public setCurrentPivot(pivot: DirectoryPivotType) {
        this._setSkipOnFilterChanged();
        this._nextFilterChangeIsNotFromUser = true;

        try {
            const newFilterState: IFilterState = this._getPivotValue<IFilterState>(
                pivot,
                "setCurrentPivot",
                () => this._allPivotFilter.getState(),
                () => this._minePivotFilter.getState()
            );

            if (newFilterState) {
                this._currentPivot = pivot;
                this._updateHubViewFilterState(newFilterState);
            }
        } finally {
            this._resetSkipOnFilterChanged();
        }
    }

    public initializePivotFilterState(pivot: DirectoryPivotType, stringifyFilterState: string): void {
        let newFilterState: IFilterState | null = null;
        const paramFilterState: IFilterState | null = this._parseIFilterState(stringifyFilterState);

        if (paramFilterState) {
            newFilterState = paramFilterState;
        }

        if (newFilterState) {
            this._setPivotFilterState(pivot, newFilterState, newFilterState);
        }

        this._setFilterInitialized(pivot);
    }

    /**
     * Updates this class' state by first resetting it to its initial state after
     * the class was instantiated, and then updating it with the parameters provided.
     * @param actionsCreator The new actions creator instance to use in this class.
     * @param hubViewState The new hub view state instance to use in this class.
     * @param currentPivot The new current pivot to use in this class.
     */
    public update(hubViewState: IVssHubViewState, currentPivot: DirectoryPivotType) {
        this._resetToUninitializedState();
        this._initializeInternals(hubViewState);
        this.setCurrentPivot(currentPivot);
    }

    public dispose() {
        this._resetToUninitializedState();
    }

    //
    //  private methods.
    //

    private _onFilterChanged = (filterState: IFilterState) => {
        //  Putting the logic in a separate method for unit-testing purposes.
        this._onFilterChangedLogic(filterState);
    };

    private _setSkipOnFilterChanged() {
        this._skipOnFilterChanged = true;
    }

    private isFilterItemStateOn(filterItemState: IFilterItemState | null, displayType: TestPlanFilterFieldType): boolean {
        let isFilterItemStateOn: boolean = false;

        if (filterItemState && filterItemState.value) {
            switch (displayType) {
                case TestPlanFilterFieldType.Text:
                    isFilterItemStateOn = (filterItemState.value as string).length > 0;
                    break;

                case TestPlanFilterFieldType.CheckboxList:
                    isFilterItemStateOn = Object.keys(filterItemState.value).length > 0;
                    break;
            }
        }

        return isFilterItemStateOn;
    }

    private _getPivotFilter(pivot: DirectoryPivotType): IFilter {
        return this._getPivotValue<IFilter>(
            pivot,
            "_getPivotFilter",
            () => this._allPivotFilter,
            () => this._minePivotFilter
        );
    }

    private _getCurrentPivotFilter(): IFilter {
        return this._getPivotFilter(this._currentPivot);
    }

    private _getFilterState(): IFilterState {
        return this._hubViewState.filter.getState();
    }

    private _initializeInternals(hubViewState: IVssHubViewState): void {
        this._hubViewState = hubViewState;
        this._hubViewState.filter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
    }

    private _resetInternalFilters(): void {
        this._allPivotFilter = new Filter();
        this._allPivotFilterInitialized = false;

        this._minePivotFilter = new Filter();
        this._minePivotFilterInitialized = false;

        this._skipOnFilterChanged = false;

        this._nextFilterChangeIsNotFromUser = false;
        this._filterHasChanged = false;
    }

    private _setPivotFilterState(pivot: DirectoryPivotType, filterState: IFilterState, defaultFilterState?: IFilterState): void {
        const filter: IFilter = this._getPivotFilter(pivot);

        filter.setState(filterState);

        if (defaultFilterState) {
            filter.setDefaultState(defaultFilterState);
        }

        if (pivot === this._currentPivot) {
            //  Need to keep the hub view's filter in sync with the current pivot's filter.
            this._updateHubViewFilterState(filterState);
        }
    }

    private _setFilterInitialized(pivot: DirectoryPivotType) {
        this._doPivotAction(
            pivot,
            "_setFilterInitialized",
            () => {
                this._allPivotFilterInitialized = true;
            },
            () => {
                this._minePivotFilterInitialized = true;
            }
        );
    }

    /**
     * Handles the onFilterChanged event.
     * @param filterState The new filter state.
     */
    private _onFilterChangedLogic(filterState: IFilterState): void {
        const filterInitialized = this._currentPivot === DirectoryPivotType.all ? this._allPivotFilterInitialized : this._minePivotFilterInitialized;

        if (!this._nextFilterChangeIsNotFromUser && filterInitialized) {
            this._filterHasChanged = true;
            if (filterState) {
                this._telemetryService.publishEvent(TelemetryConstants.areaTestManagement, TelemetryConstants.featureNewTestPlan_FilterClick, {
                    Tab: this._currentPivot,
                    FilterKey: Object.keys(filterState)[0]
                });
            }
        } else {
            this._nextFilterChangeIsNotFromUser = false;
        }

        if (this._reactToFilterChanged()) {
            this._saveFilterState();
            this._onFilterChangedFunc(this._currentPivot, filterState);
        }
    }

    /**
     *  Returns a value indicating whether or not this class should react to changes on the hub view filter's state.
     *  Reasons to ignore filter state changes:
     *  1. If the filter hasn't been initialized. This avoids overwriting a saved filter's state with an empty one
     *     and filtering items on the store unnecessarily.
     *  2. If requested explicitly by using the _skipOnFilterChanged flag.
     */
    private _reactToFilterChanged(): boolean {
        return this._isFilterInitialized() && !this._skipOnFilterChanged;
    }

    private _resetToUninitializedState() {
        this._resetInternalFilters();

        //TODO: arvindkr - check if required
        //this._currentPivot = null;
        //this._actionsCreator = null;

        if (this._hubViewState) {
            this._hubViewState.filter.unsubscribe(this._onFilterChanged);
        }
    }

    private _updateHubViewFilterState(newFilterState: IFilterState) {
        this._hubViewState.filter.setState(newFilterState);
        this._hubViewState.viewOptions.setViewOption(HubViewOptionKeys.showFilterBar, this.isFilterStateOn(newFilterState));
    }

    /**
     *  Returns a value indicating whether or not to persist the filter state for the pivot at the server side.
     */
    private _shouldPersistFilterState(pivot: DirectoryPivotType): boolean {
        const pivotFilter: IFilter = this._getPivotFilter(pivot);
        return pivotFilter.hasChangesToReset();
    }

    private _isFilterInitialized(): boolean {
        return this._getPivotValue<boolean>(
            this._currentPivot,
            "_isFilterInitialized",
            () => this._allPivotFilterInitialized,
            () => this._minePivotFilterInitialized
        );
    }

    private _saveFilterState(): void {
        const filterStateToSave: IFilterState = this._getFilterState();
        const currentPivot: DirectoryPivotType = this._currentPivot;

        //  Update current pivot's filter state.
        this._getCurrentPivotFilter().setState(filterStateToSave);

        //  Save filter state in registry key.
        this._saveState(currentPivot, filterStateToSave);
    }

    private _saveState(pivot: DirectoryPivotType, state: IFilterState): void {
        if (this._shouldPersistFilterState(pivot)) {
            this._persistFilterState(pivot, state);
        }
    }

    private _persistFilterState(pivot: DirectoryPivotType, state: IFilterState) {
        this._doPivotAction(
            pivot,
            "_persistFilterState",
            () => this.saveFilterStateToServer(DirectoryPivotType.all, state),
            () => this.saveFilterStateToServer(DirectoryPivotType.mine, state)
        );
    }

    private _parseIFilterState(stringifyVersion: string): IFilterState | null {
        let result: IFilterState | null = null;

        if (stringifyVersion && stringifyVersion.length > 0) {
            try {
                result = JSON.parse(stringifyVersion);
            } catch (error) {}
        }

        return result;
    }

    private _resetSkipOnFilterChanged() {
        this._skipOnFilterChanged = false;
    }

    private _getPivotValue<T>(pivot: DirectoryPivotType, source: string, allPivotAction: () => T, minePivotAction: () => T) {
        let result: T | null = null;

        switch (pivot) {
            case DirectoryPivotType.all:
                result = allPivotAction();
                break;
            case DirectoryPivotType.mine:
                result = minePivotAction();
                break;
        }

        return result!;
    }

    private _doPivotAction(pivot: DirectoryPivotType, source: string, allPivotAction: () => void, minePivotAction: () => void) {
        switch (pivot) {
            case DirectoryPivotType.all:
                allPivotAction();
                break;
            case DirectoryPivotType.mine:
                minePivotAction();
                break;
        }
    }

    /**
     * Save a filter state to the server
     * @param filterState The filter state to save
     */
    public saveFilterStateToServer(pivot: string, filterState: IFilterState): void {
        // Note that if  specifying onSaveCallback, we will save the state immediatelly (it is not throttled)
        const { success, serializedFilter } = this._serializeFilter(pivot, filterState);

        this._timerManagementContext.dispose();
        this._timerManagementContext.throttle;

        if (success) {
            this._throttledSaveFilter(pivot, filterState, serializedFilter);
        }
    }

    private _serializeFilter(pivot: string, filterState: IFilterState): { success: boolean; serializedFilter: string } {
        let success = true;
        let serializedFilter: string = "";
        try {
            serializedFilter = JSON.stringify(filterState || {});
        } catch (error) {
            //TODO:arvindkr: Handle errors here
        }

        return {
            success,
            serializedFilter
        };
    }

    private _setFilterState = (pivot: string, filterState: IFilterState, serializedFilter: string): void => {
        var testPlanHubSettingService = this._pageContext.getService<ITestPlanHubSettingService>("ITestPlanHubSettingService");
        testPlanHubSettingService.setFilterState(pivot as DirectoryPivotType, serializedFilter);
    };

    private get _telemetryService() {
        return this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    private _allPivotFilter: IFilter;
    private _allPivotFilterInitialized: boolean;

    private _minePivotFilter: IFilter;
    private _minePivotFilterInitialized: boolean;

    private _currentPivot: DirectoryPivotType;

    private _skipOnFilterChanged: boolean;

    private _filterHasChanged: boolean;
    private _nextFilterChangeIsNotFromUser: boolean;
    private _hubViewState: IVssHubViewState;
    private _pageContext: IVssPageContext;
    private _onFilterChangedFunc: (pivot: DirectoryPivotType, filterState: IFilterState) => void;

    private _projectId: string;
    private _hubName: string;
    private _timerManagementContext: TimerManagement;
    private _throttledSaveFilter: (pivot: string, filterState: IFilterState, serializedFilter: string) => void;
}

Services.add("testPlanFilterManager", { serviceFactory: TestPlanFilterManager });
